<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount, onMounted, type Component, type ComponentPublicInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAppStore, type NavContext } from '@/core/stores/useAppStore'
import { useAuthStore } from '@/modules/auth/stores/useAuthStore'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { PERMISSIONS } from '@/core/permissions/constants'
import { useNotificationSlideOver } from '@/modules/notifications/composables/useNotificationSlideOver'
import ProjectSwitcher from './ProjectSwitcher.vue'
import {
  IconLayoutDashboard,
  IconFolders,
  IconUsers,
  IconFileText,
  IconShoppingBag,
  IconAddressBook,
  IconFileInvoice,
  IconMail,
  IconRobot,
  IconHistory,
  IconLogout,
} from '@tabler/icons-vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const appStore = useAppStore()
const authStore = useAuthStore()
const projectsStore = useProjectsStore()
const notifSlide = useNotificationSlideOver()

// --- Route watcher (moved from sidebar) ---
watch(() => route.path, (path) => {
  appStore.syncContextFromRoute(path)
}, { immediate: true })

// --- Data types ---
interface FlyoutChild {
  label: string
  path?: string
  action?: 'notifSlide'
  permission?: string
  projectScoped?: boolean
  sectionLabel?: string
}

interface RailItem {
  id: NavContext
  icon: Component
  label: string
  permission?: string
  projectScoped?: boolean
  directRoute?: string
  children?: FlyoutChild[]
}

// --- Navigation items ---
const globalItems: RailItem[] = [
  { id: 'dashboard', icon: IconLayoutDashboard, label: 'common.nav.dashboard', directRoute: '/' },
  { id: 'projects', icon: IconFolders, label: 'common.nav.projects', permission: PERMISSIONS.PROJECT.LIST, directRoute: '/projects' },
  { id: 'users', icon: IconUsers, label: 'common.nav.users', permission: PERMISSIONS.USER.LIST, directRoute: '/users' },
  { id: 'audit-log', icon: IconHistory, label: 'common.nav.auditLog', permission: PERMISSIONS.AUDIT.VIEW, directRoute: '/audit-log' },
]

const projectItems: RailItem[] = [
  {
    id: 'content', icon: IconFileText, label: 'common.nav.content',
    permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true,
    children: [
      { label: 'common.nav.pages', path: '/pages', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.menus', path: '/menus', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.videos', path: '/videos', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.categories', path: '/categories', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.mediaLibrary', path: '/assets', permission: PERMISSIONS.MEDIA.VIEW, projectScoped: true },
      { label: 'common.nav.forms', path: '/forms', permission: PERMISSIONS.SETTINGS.VIEW, projectScoped: true },
    ],
  },
  {
    id: 'sales', icon: IconShoppingBag, label: 'common.nav.sales',
    permission: PERMISSIONS.ESHOP.VIEW, projectScoped: true,
    children: [
      { sectionLabel: 'common.nav.catalog', label: 'common.nav.products', path: '/products', permission: PERMISSIONS.ESHOP.VIEW, projectScoped: true },
      { label: 'common.nav.aurum', path: '/aurum', permission: PERMISSIONS.ESHOP.MANAGE, projectScoped: true },
      { label: 'common.nav.aurumCheckIn', path: '/aurum/check-in', permission: PERMISSIONS.ESHOP.VIEW, projectScoped: true },
      { label: 'common.nav.categories', path: '/categories', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { sectionLabel: 'common.nav.shop', label: 'common.nav.orders', path: '/orders', permission: PERMISSIONS.ESHOP.ORDERS, projectScoped: true },
      { label: 'common.nav.customers', path: '/customers', permission: PERMISSIONS.ESHOP.CUSTOMERS, projectScoped: true },
      { label: 'common.nav.tickets', path: '/tickets', permission: PERMISSIONS.TICKETS.LIST, projectScoped: true },
      { sectionLabel: 'common.nav.training', label: 'common.nav.eventDates', path: '/event-dates', permission: PERMISSIONS.ESHOP.MANAGE, projectScoped: true },
    ],
  },
  {
    id: 'contacts', icon: IconAddressBook, label: 'common.nav.contacts',
    permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true,
    children: [
      { label: 'common.nav.contactPersons', path: '/contacts', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
      { label: 'common.nav.groups', path: '/contact-groups', permission: PERMISSIONS.CONTENT.VIEW, projectScoped: true },
    ],
  },
  {
    id: 'invoicing', icon: IconFileInvoice, label: 'common.nav.invoicing',
    permission: PERMISSIONS.INVOICING.VIEW, projectScoped: true,
    children: [
      { label: 'common.nav.issuedInvoices', path: '/invoicing/invoices', permission: PERMISSIONS.INVOICING.VIEW, projectScoped: true },
      { label: 'common.nav.clientDirectory', path: '/invoicing/clients', permission: PERMISSIONS.INVOICING.CLIENTS, projectScoped: true },
    ],
  },
]

const systemItems: RailItem[] = [
  {
    id: 'comms', icon: IconMail, label: 'common.nav.communications',
    projectScoped: true,
    children: [
      { label: 'common.nav.sendNotification', action: 'notifSlide', permission: PERMISSIONS.ESHOP.MANAGE, projectScoped: true },
      { label: 'common.nav.emailTemplates', path: '/email-templates', permission: PERMISSIONS.SETTINGS.VIEW, projectScoped: true },
    ],
  },
  {
    id: 'ai', icon: IconRobot, label: 'common.nav.aiAutomation',
    children: [
      { label: 'common.nav.orchestration', path: '/orchestration', permission: PERMISSIONS.ORCHESTRATION.VIEW },
    ],
  },
]

// --- Permission helpers ---
function hasPerm(permission: string, projectScoped = false) {
  if (projectScoped) {
    return authStore.hasPermission(permission, projectsStore.currentProjectPermissions)
  }
  return authStore.hasPermission(permission)
}

// --- Filtered items ---
const filteredGlobalItems = computed(() =>
  globalItems.filter(item => !item.permission || hasPerm(item.permission))
)

const filteredProjectItems = computed(() => {
  if (!projectsStore.currentProject) return []
  return projectItems.filter(item => {
    if (item.id === 'invoicing' && !projectsStore.currentProject?.invoicingEnabled) return false
    return !item.permission || hasPerm(item.permission, !!item.projectScoped)
  })
})

const filteredSystemItems = computed(() => {
  const items = !projectsStore.currentProject
    ? systemItems.filter(item => !item.projectScoped)
    : systemItems
  return items.filter(item => {
    if (item.permission && !hasPerm(item.permission, !!item.projectScoped)) return false
    if (item.children?.length) return visibleChildren(item).length > 0
    return true
  })
})

// --- Visible children (permission-filtered) ---
function visibleChildren(item: RailItem): FlyoutChild[] {
  if (!item.children) return []
  return item.children.filter(child => {
    if (!child.permission) return true
    return hasPerm(child.permission, !!child.projectScoped)
  })
}

// --- Hover flyout logic ---
const hoveredItem = ref<NavContext | null>(null)
const flyoutTop = ref(12)
const flyoutPanel = ref<HTMLElement | null>(null)
const flyoutAnchor = ref<HTMLElement | null>(null)
let hoverTimeout: ReturnType<typeof setTimeout> | null = null

function setFlyoutPanel(el: Element | ComponentPublicInstance | null) {
  flyoutPanel.value = el instanceof HTMLElement ? el : null
}

function updateFlyoutPosition() {
  const anchor = flyoutAnchor.value
  if (!anchor) return

  const viewportPadding = 12
  const anchorRect = anchor.getBoundingClientRect()
  const content =
    flyoutPanel.value?.querySelector<HTMLElement>('.flyout-content')
  const contentHeight = content?.offsetHeight ?? 0
  const maxTop = window.innerHeight - viewportPadding - contentHeight

  flyoutTop.value = Math.max(viewportPadding, Math.min(anchorRect.top, maxTop))
}

function onItemEnter(item: RailItem, event: MouseEvent) {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
    hoverTimeout = null
  }
  if (item.children?.length) {
    flyoutAnchor.value = event.currentTarget as HTMLElement
    flyoutTop.value = Math.max(
      12,
      flyoutAnchor.value.getBoundingClientRect().top
    )
    hoveredItem.value = item.id
    nextTick(updateFlyoutPosition)
  }
}

function onItemLeave() {
  hoverTimeout = setTimeout(() => {
    hoveredItem.value = null
    flyoutAnchor.value = null
  }, 100)
}

// --- Click handlers ---
function handleRailClick(item: RailItem) {
  if (item.directRoute) {
    router.push(item.directRoute)
  }
  appStore.setActiveContext(item.id)
}

function handleChildClick(child: FlyoutChild) {
  hoveredItem.value = null
  flyoutAnchor.value = null
  if (child.action === 'notifSlide') {
    notifSlide.open()
    return
  }
  if (child.path) {
    router.push(child.path)
  }
}

// --- Active route check ---
function isActive(path: string) {
  return route.path === path || route.path.startsWith(`${path}/`)
}

// --- Project switcher ---
const showProjectSwitcher = ref(false)

// --- User menu ---
const showUserMenu = ref(false)

function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value
}

function handleLogout() {
  showUserMenu.value = false
  authStore.logout()
}

onMounted(() => {
  window.addEventListener('resize', updateFlyoutPosition)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateFlyoutPosition)
})

// --- User / env ---
const userInitial = computed(() =>
  authStore.user?.name?.charAt(0).toUpperCase() || 'U'
)

const envInfo = computed(() => {
  const hostname = window.location.hostname
  const parts = hostname.split('.')
  const subdomain = parts[0]
  const parentDomain = parts.slice(1).join('.')

  let env = 'production'
  let envLabel = 'PROD'
  let dotColor = 'bg-green-400'
  let textColor = 'text-green-400'

  if (hostname.includes('.local') || hostname === 'localhost') {
    env = 'local'
    envLabel = 'DEV'
    dotColor = 'bg-blue-400'
    textColor = 'text-blue-400'
  } else if (hostname.includes('.test.')) {
    env = 'test'
    envLabel = 'TEST'
    dotColor = 'bg-purple-400'
    textColor = 'text-purple-400'
  } else if (hostname.includes('.staging.')) {
    env = 'staging'
    envLabel = 'STG'
    dotColor = 'bg-yellow-400'
    textColor = 'text-yellow-400'
  }

  let apiUrl: string
  const prefixMatch = subdomain.match(/^(.+)-(admin|partner|coin)$/)
  if (prefixMatch) {
    const prefix = prefixMatch[1]
    apiUrl = `${prefix}-api.${parentDomain}`
  } else {
    apiUrl = `api.${parentDomain}`
  }

  return { env, envLabel, dotColor, textColor, apiUrl, hostname }
})
</script>

<template>
  <nav class="w-16 min-w-[64px] bg-base border-r border-white/[0.06] flex flex-col items-center py-3 z-50">
    <!-- Logo / Project Switcher -->
    <div class="relative mb-5">
      <button
        class="w-[38px] h-[38px] rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 cursor-pointer hover:bg-amber-500/30 hover:border-amber-500/40 transition-all duration-150"
        @click="showProjectSwitcher = !showProjectSwitcher"
      >
        <span class="font-display font-bold text-sm">{{ projectsStore.currentProject?.name?.charAt(0)?.toUpperCase() || '?' }}</span>
      </button>
      <ProjectSwitcher v-if="showProjectSwitcher" @close="showProjectSwitcher = false" />
    </div>

    <!-- Navigation -->
    <div class="flex flex-col items-center gap-1 flex-1">
      <!-- Global items -->
      <div
        v-for="item in filteredGlobalItems"
        :key="item.id"
        class="relative"
        @mouseenter="onItemEnter(item, $event)"
        @mouseleave="onItemLeave()"
      >
        <button
          class="rail-btn"
          :class="{ active: appStore.activeContext === item.id }"
          @click="handleRailClick(item)"
        >
          <component :is="item.icon" class="w-5 h-5" />
          <span v-if="!item.children?.length" class="tip">{{ t(item.label) }}</span>
        </button>
      </div>

      <!-- Separator -->
      <div v-if="filteredProjectItems.length > 0" class="w-6 h-px bg-white/[0.06] my-2"></div>

      <!-- Project-scoped items -->
      <div
        v-for="item in filteredProjectItems"
        :key="item.id"
        class="relative"
        @mouseenter="onItemEnter(item, $event)"
        @mouseleave="onItemLeave()"
      >
        <button
          class="rail-btn"
          :class="{ active: appStore.activeContext === item.id }"
          @click="handleRailClick(item)"
        >
          <component :is="item.icon" class="w-5 h-5" />
          <span v-if="!item.children?.length" class="tip">{{ t(item.label) }}</span>
        </button>

        <!-- Flyout -->
        <div
          v-if="item.children?.length && hoveredItem === item.id"
          :ref="setFlyoutPanel"
          class="flyout-panel"
          :style="{ top: `${flyoutTop}px` }"
        >
          <div class="flyout-content">
            <div class="px-3 py-1.5 text-xs font-semibold text-gray-200">{{ t(item.label) }}</div>
            <template v-for="(child, idx) in visibleChildren(item)" :key="idx">
              <div v-if="child.sectionLabel" class="text-2xs font-bold uppercase tracking-wider text-gray-500/50 px-5 pt-3 pb-1">
                {{ t(child.sectionLabel) }}
              </div>
              <a
                class="nav-item"
                :class="{ active: child.path && isActive(child.path) }"
                @click.prevent="handleChildClick(child)"
              >
                {{ t(child.label) }}
              </a>
            </template>
          </div>
        </div>
      </div>

      <!-- Separator -->
      <div v-if="filteredSystemItems.length > 0" class="w-6 h-px bg-white/[0.06] my-2"></div>

      <!-- System items -->
      <div
        v-for="item in filteredSystemItems"
        :key="item.id"
        class="relative"
        @mouseenter="onItemEnter(item, $event)"
        @mouseleave="onItemLeave()"
      >
        <button
          class="rail-btn"
          :class="{ active: appStore.activeContext === item.id }"
          @click="handleRailClick(item)"
        >
          <component :is="item.icon" class="w-5 h-5" />
          <span v-if="!item.children?.length" class="tip">{{ t(item.label) }}</span>
        </button>

        <!-- Flyout -->
        <div
          v-if="item.children?.length && hoveredItem === item.id"
          :ref="setFlyoutPanel"
          class="flyout-panel"
          :style="{ top: `${flyoutTop}px` }"
        >
          <div class="flyout-content">
            <div class="px-3 py-1.5 text-xs font-semibold text-gray-200">{{ t(item.label) }}</div>
            <template v-for="(child, idx) in visibleChildren(item)" :key="idx">
              <a
                class="nav-item"
                :class="{ active: child.path && isActive(child.path) }"
                @click.prevent="handleChildClick(child)"
              >
                {{ t(child.label) }}
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Environment indicator + User avatar -->
    <div class="flex flex-col items-center gap-2">
      <!-- Env badge -->
      <div class="group relative">
        <div class="flex flex-col items-center gap-1 cursor-default">
          <span class="w-2 h-2 rounded-full" :class="envInfo.dotColor"></span>
          <span class="text-2xs font-bold tracking-wider leading-none" :class="envInfo.textColor">{{ envInfo.envLabel }}</span>
        </div>

        <!-- Hover tooltip -->
        <div
          class="absolute bottom-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-[60] border border-white/10 bg-base-100 rounded-lg py-2.5 px-3.5 whitespace-nowrap shadow-xl"
          style="left: calc(100% + 12px);"
        >
          <div class="text-xs font-semibold text-gray-200 mb-1.5">{{ envInfo.env }}</div>
          <div class="space-y-1 text-2xs text-gray-400">
            <div class="flex items-center gap-1.5">
              <span class="text-gray-500">API</span>
              <span class="text-gray-300">{{ envInfo.apiUrl }}</span>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-gray-500">Host</span>
              <span class="text-gray-300">{{ envInfo.hostname }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- User avatar -->
      <div class="relative">
        <button
          class="w-[34px] h-[34px] rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-xs font-bold text-amber-400 cursor-pointer hover:bg-amber-500/30 hover:border-amber-500/40 transition-all duration-150"
          :title="authStore.user?.name || 'User'"
          @click="toggleUserMenu"
        >
          {{ userInitial }}
        </button>

        <!-- User popover menu -->
        <Teleport to="body">
          <div
            v-if="showUserMenu"
            class="fixed inset-0 z-[99]"
            @click="showUserMenu = false"
          ></div>
        </Teleport>
        <div
          v-if="showUserMenu"
          class="absolute bottom-0 z-[100] border border-white/10 bg-base-100 rounded-lg py-3 px-4 shadow-xl min-w-[200px]"
          style="left: calc(100% + 12px);"
        >
          <div class="mb-3">
            <div class="text-sm font-semibold text-gray-200 truncate">{{ authStore.user?.name || 'User' }}</div>
            <div class="text-xs text-gray-500 truncate">{{ authStore.user?.email || '' }}</div>
          </div>
          <div class="border-t border-white/[0.06] pt-2">
            <button
              class="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-gray-400 hover:bg-white/[0.06] hover:text-gray-200 cursor-pointer transition-colors"
              @click="handleLogout"
            >
              <IconLogout class="w-4 h-4" />
              {{ t('common.nav.logout') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>
